import { setAccessToken } from "@/services/api";
import { UserDto, userService } from "@/services/userService";
import { useAuthStore } from "@/store/authStore";
import { RoleEnum } from "@/types";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import { AuthService, setOnLogout } from "../services/authService";
import { logger } from "../utils/logger";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean; // Add loading state
  login: (
    email: string,
    password: string,
    remember?: boolean
  ) => Promise<{ success: boolean; role: RoleEnum | null }>;
  logout: () => void; // Change back to sync
  user: UserDto | null;
  role: RoleEnum | undefined;
  updateUser: (user: UserDto | null) => void;
  updateRole: (role: RoleEnum | undefined) => void;
  refreshToken: () => Promise<boolean>;
  forgotPassword: (
    email: string
  ) => Promise<{ success: boolean; message: string }>;
  resetPassword: (
    token: string,
    data: { password: string; confirm_password: string }
  ) => Promise<{ success: boolean; message: string }>;
  validateResetToken: (
    token: string
  ) => Promise<{ valid: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [user, setUser] = useState<UserDto | null>(null);
  const [role, setRole] = useState<RoleEnum>();
  const { setUser: setUserStore } = useAuthStore();

  useEffect(() => {
    const fetchRefresh = async () => {
      try {
        await refreshToken();
        setIsLoading(false);
      } catch (err) {
        console.error("Refresh token failed:", err);
      }
    };

    fetchRefresh();
  }, []);

  // Set triggerLogout for api.ts
  useEffect(() => {
    setOnLogout(logout);
  }, []);

  const login = async (
    email: string,
    password: string,
    remember: boolean = false
  ): Promise<{ success: boolean; role: RoleEnum | null }> => {
    try {
      const { accessToken } = await AuthService.login({
        email,
        password,
        remember: remember,
        site: 2,
      });
      setAccessToken(accessToken);
      setIsAuthenticated(true);
      const userProfile = await userService.getUser();
      setRole(userProfile.role?.name);
      setUser(userProfile);
      setUserStore(userProfile);
      return { success: true, role: userProfile.role?.name };
    } catch (error: any) {
      toast.error(error.message);
      return { success: false, role: null };
    }
  };

  const logout = () => {
    // Clear local state
    AuthService.logout()
      .then(() => {
        setIsAuthenticated(false);
        setUserStore(null);
        setUser(null);
        setRole(undefined);
        setAccessToken(null);
      })
      .catch((error) => {
        logger.error("Logout error:", error);
      });
  };

  const updateUser = (user: UserDto | null) => {
    setUser(user);
  };

  const updateRole = (role: RoleEnum | undefined) => {
    setRole(role);
  };

  // Refresh token method - đơn giản, chỉ gọi API
  const refreshToken = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      // Backend tự lấy refresh token từ httpOnly cookie
      const { accessToken } = await AuthService.refreshToken();
      setAccessToken(accessToken);
      setIsAuthenticated(true);
      const userProfile = await userService.getUser();
      setRole(userProfile.role?.name);
      setUser(userProfile);
      setUserStore(userProfile);
      setIsLoading(false);
      return true;
    } catch (error: any) {
      logger.error("❌ Refresh token failed:", error);
      setIsLoading(false);
      // Refresh failed, logout user
      // TODO: interceptors (api.ts) đã xử lý redirect về login
      // logout();
      return false;
    }
  };

  const forgotPassword = async (
    email: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await AuthService.forgotPassword(email);
      return { success: true, message: result.message };
    } catch (error: any) {
      // Extract error message from backend response
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "エラーが発生しました。もう一度お試しください。";
      return { success: false, message: errorMessage };
    }
  };

  const validateResetToken = async (
    token: string
  ): Promise<{ valid: boolean; message: string }> => {
    try {
      const result = await AuthService.verifyResetToken(token);
      return { valid: result.valid, message: result.message };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "トークンの検証でエラーが発生しました。";
      return { valid: false, message: errorMessage };
    }
  };

  const resetPassword = async (
    token: string,
    data: { password: string; confirm_password: string }
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await AuthService.resetPassword({
        password: data.password,
        confirm_password: data.confirm_password,
        token,
      });
      return { success: true, message: result.message };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "パスワードリセットでエラーが発生しました。もう一度お試しください。";
      return { success: false, message: errorMessage };
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    user,
    role,
    updateUser,
    updateRole,
    refreshToken, // Add refreshToken method
    forgotPassword,
    resetPassword,
    validateResetToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
